<template>
  <!-- <div class="font-main bg-surface-100! dark:bg-surface-800!"> -->
  <div
    class="font-main bg-linear-to-b! from-surface-300 via-surface-100 to-surface-100 dark:from-surface-900 dark:via-surface-800 dark:to-surface-800 from-0% via-30% to-100%"
  >
    <RouterView v-slot="{ Component }">
      <Transition v-bind="fadeTransition">
        <component :is="Component"></component>
      </Transition>
    </RouterView>
    <Toast class="w-[450px]" position="bottom-center">
      <template #message="slotProps">
        <i class="text-2xl text-surface my-auto mr-1 pi" :class="slotProps.message.icon"></i>
        <div class="text-base leading-none ml-2 flex-1 my-auto">
          <span class="font-bold block">{{ slotProps.message.summary }}</span>
          <div v-if="slotProps.message.detail" class="block mt-2 text-balance whitespace-pre-line">{{ slotProps.message.detail }}</div>
        </div>
      </template>
    </Toast>
    <ConfirmDialog :draggable="false" class="w-96! text-center" pt:content:class="pb-3!" pt:footer:class="pb-3!">
      <template #message="slotProps">
        <div class="flex flex-column align-items-center w-80 gap-3 border-dashed border-b-2 border-surface text-center mx-auto py-3">
          <i class="text-3xl text-surface my-auto" :class="slotProps.message.icon"></i>
          <p class="text-balance whitespace-pre-line">{{ slotProps.message.message }}</p>
        </div>
      </template>
    </ConfirmDialog>
  </div>
  <Transition v-bind="fadeTransition">
    <div
      v-if="systemStore.isGlobalLoading"
      class="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-surface-900/50 backdrop-blur-sm"
    >
      <ProgressSpinner strokeWidth="4" class="w-16 h-16" />
      <p class="text-white font-bold mt-4 text-lg">{{ systemStore.loadingMessage }}</p>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { RouterView } from "vue-router";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";
import { fadeTransition } from "./services/Transitions";
import { getCssColorVariable } from "./services/Utility";
import { registerTheme, use } from "echarts/core";
import { usePropsStore } from "./stores/props";
import { useSystemStore } from "@/stores/systemStore";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from "echarts/components";

use([CanvasRenderer, BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent]);

const propsStore = usePropsStore();
const systemStore = useSystemStore();

const updateThemes = () => {
  const CUSTOM_DARK_THEME = {
    backgroundColor: "transparent",

    title: {
      left: "left",
      textStyle: {
        color: getCssColorVariable("--color-surface-100"),
        fontSize: 20,
        lineHeight: 5,
      },
      subtextStyle: {
        color: getCssColorVariable("--color-surface-400"),
        fontSize: 15,
        lineHeight: -10,
      },
    },

    categoryAxis: {
      axisLabel: {
        color: getCssColorVariable("--color-surface-200"),
      },
      axisLine: {
        lineStyle: {
          show: true,
          color: getCssColorVariable("--color-surface-400"),
          type: "solid",
          width: 2,
        },
      },
    },

    valueAxis: {
      axisLabel: {
        color: getCssColorVariable("--color-surface-200"),
      },
      splitLine: {
        lineStyle: {
          color: getCssColorVariable("--color-surface-400"),
          type: "dashed",
          width: 2,
        },
      },
    },

    tooltip: {
      backgroundColor: getCssColorVariable("--color-surface-900"),

      textStyle: {
        color: getCssColorVariable("--color-surface-200"),
      },
      borderColor: getCssColorVariable("--color-surface-600"),
      borderWidth: 2,
    },

    legend: {
      textStyle: {
        color: getCssColorVariable("--color-surface-200"),
        fontWeight: "bold",
      },
    },
  };
  registerTheme("customDark", CUSTOM_DARK_THEME);

  const CUSTOM_LIGHT_THEME = {
    backgroundColor: "transparent",

    animation: true, // Ensure animations are enabled (default is true)
    animationDuration: 1500, // Duration of the initial animation in milliseconds (e.g., 1.5 seconds)
    animationEasing: "cubicInOut", // Easing function for smoother animation (e.g., 'linear', 'quadraticOut', 'cubicInOut')
    animationDelay: 500,
    title: {
      left: "left",
      textStyle: {
        color: getCssColorVariable("--color-surface-700"),
        fontSize: 20,
        lineHeight: 5,
      },
      subtextStyle: {
        color: getCssColorVariable("--color-surface-500"),
        fontSize: 15,
        lineHeight: -10,
      },
    },

    categoryAxis: {
      axisLabel: {
        color: getCssColorVariable("--color-surface-700"),
      },
      axisLine: {
        lineStyle: {
          show: true,
          color: getCssColorVariable("--color-surface-300"),
          type: "solid",
          width: 2,
        },
      },
    },

    valueAxis: {
      axisLabel: {
        color: getCssColorVariable("--color-surface-700"),
      },
      splitLine: {
        lineStyle: {
          color: getCssColorVariable("--color-surface-300"),
          type: "dashed",
          width: 2,
        },
      },
    },

    tooltip: {
      backgroundColor: getCssColorVariable("--color-surface-100"),

      textStyle: {
        color: getCssColorVariable("--color-surface-700"),
      },
      borderColor: getCssColorVariable("--color-surface-300"),
      borderWidth: 2,
    },

    legend: {
      textStyle: {
        color: getCssColorVariable("--color-surface-700"),
        fontWeight: "bold",
      },
    },
  };
  registerTheme("customLight", CUSTOM_LIGHT_THEME);
};

updateThemes();
</script>
